import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Icon, Platform } from '../components/Icons.jsx';
import { api } from '../services/api/client.js';
import { formatAppDate } from '../utils/locale.js';
import { friendlyErrorMessage, notify } from '../lib/notifications.js';
import { DeviceTimezoneLabel } from '../components/DeviceTimezoneLabel.jsx';

const WEEK_START = 1;
const VIEW_MODES = ['month', 'week', 'day', 'agenda'];
// TASK-331 phase-1-fixes round 4: mirrors MISSED_WHILE_LAPSED_ERROR_CODE in
// publish-dispatcher-worker (src/index.ts ~1344, sweepRenewedOrgs) — the
// last_error_code stamped on a past-dated target that was skipped while the
// org's plan was lapsed. See mapSchedulerEvent's `missedWhileLapsed` derivation.
const MISSED_WHILE_LAPSED_ERROR_CODE = 'missed_while_lapsed';
const STATUS_FILTERS = [
  ['all', 'filterAll'],
  ['approved', 'filterApproved'],
  ['publishing', 'filterPublishing'],
  ['published', 'filterPublished'],
  ['failed', 'filterFailed'],
  ['partial_failure', 'filterPartialFailure'],
  // TASK-331 (M7 fix): a lapsed org's queued posts are parked here by the
  // publish-dispatcher instead of being published — 'filterBlockedBilling' is
  // the one entry in this array that IS localized via t() (see the render
  // below), unlike the plain-English STATUS_LABELS/SHORT/PILL maps.
  ['blocked_billing', 'filterBlockedBilling'],
];
// TASK-331 phase-1-fixes round 4: the dedicated 'missed' status (round 3) was
// retired backend-side — too wide a blast radius (@dathent-sro/shared-types'
// PublishTargetStatus union lives outside this task's repo set, so widening
// it locally was a one-off fork). A past-dated target that was blocked while
// the plan was lapsed now settles as a REGULAR 'failed' target with
// last_error_code='missed_while_lapsed' (publish-dispatcher-worker
// sweepRenewedOrgs). It renders as a normal failed post everywhere EXCEPT the
// small label override in StatusBadge below, which swaps in a "skipped while
// lapsed" phrase using last_error_code — present on every target payload this
// screen already receives (scheduler-worker src/index.ts, listPosts mapper:
// `lastErrorCode: row.last_error_code`).
// TASK-331 (M7 fix): STATUS_LABELS/STATUS_SHORT_LABELS/STATUS_PILL_LABELS are
// plain hardcoded English for every existing status (this file never
// localizes them — only the STATUS_FILTERS chip labels above go through
// t()). 'blocked_billing' follows that same established convention rather
// than introducing a one-off i18n path in an otherwise non-localized object.
const STATUS_LABELS = {
  draft: 'Draft',
  generating: 'Generating',
  ready: 'Ready',
  approved: 'Approved',
  scheduled: 'Scheduled',
  publishing: 'Publishing',
  queued: 'Publishing',
  published: 'Published',
  failed: 'Failed',
  partial_failure: 'Partial failure',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  // A target the backend could not settle either way — it may or may not have
  // reached the platform. Without these two entries lifecycleSummary's last
  // branch printed the raw string "needs_manual_action" at the user.
  inbox: 'Needs your attention',
  needs_manual_action: 'Needs your attention',
  blocked_billing: 'Paused (plan lapsed)',
};
const STATUS_SHORT_LABELS = {
  draft: 'Draft',
  generating: 'Gen',
  ready: 'Ready',
  approved: 'Ready',
  scheduled: 'Sched',
  publishing: 'Posting',
  queued: 'Posting',
  published: 'Posted',
  failed: 'Fail',
  partial_failure: 'Partial',
  rejected: 'Reject',
  cancelled: 'Cancel',
  inbox: 'Check',
  needs_manual_action: 'Check',
  blocked_billing: 'Paused',
};
const STATUS_PILL_LABELS = {
  draft: 'DRAFT',
  generating: 'GEN',
  ready: 'READY',
  approved: 'READY',
  scheduled: 'SCHED',
  publishing: 'POSTING',
  queued: 'POSTING',
  published: 'POSTED',
  failed: 'FAILED',
  partial_failure: 'PARTIAL',
  rejected: 'REJECTED',
  cancelled: 'CANCELLED',
  inbox: 'CHECK',
  needs_manual_action: 'CHECK',
  blocked_billing: 'PAUSED',
};

export const Scheduler = ({ navigate, openPost }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const mobileDefault = typeof window !== 'undefined' && window.innerWidth < 768;
  const view = normalizeView(searchParams.get('view'), mobileDefault ? 'agenda' : 'month');
  const focusPostId = searchParams.get('focus') || null;
  const selectedDate = parseDateParam(searchParams.get('date')) || startOfDay(new Date());
  const filter = normalizeFilter(searchParams.get('status'));
  const visibleRange = useMemo(() => rangeForView(view, selectedDate), [view, selectedDate.getTime()]);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workingId, setWorkingId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [inspectorOpen, setInspectorOpen] = useState(false);

  useEffect(() => {
    function handleKey(event) {
      if (event.key === 'Escape' && inspectorOpen && !selectedEvent) setInspectorOpen(false);
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [inspectorOpen, selectedEvent]);

  const visibleEvents = useMemo(() => {
    const rangeFiltered = events.filter(event => event.localDateKey >= dateKey(visibleRange.start) && event.localDateKey <= dateKey(visibleRange.end));
    return filter === 'all' ? rangeFiltered : rangeFiltered.filter(event => statusFilterMatch(event.status, filter));
  }, [events, filter, visibleRange.start.getTime(), visibleRange.end.getTime()]);
  const eventsByDate = useMemo(() => groupEventsByDate(visibleEvents), [visibleEvents]);
  const counts = useMemo(() => countStatuses(events), [events]);

  useEffect(() => {
    let cancelled = false;
    async function loadSchedule() {
      try {
        setLoading(true);
        setError(null);
        const fetchRange = calendarFetchRange(selectedDate);
        const response = await api.scheduler.getCalendar(fetchRange.from.toISOString(), fetchRange.to.toISOString(), { status: filter, timezone });
        if (!cancelled) setEvents((response.posts || []).map(row => mapSchedulerEvent(row, timezone)).sort(compareEvents));
      } catch (err) {
        if (!cancelled) setError(friendlyErrorMessage(err, 'Could not load scheduler data.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadSchedule();
    return () => { cancelled = true; };
  }, [selectedDate.getFullYear(), selectedDate.getMonth(), timezone]);

  useEffect(() => {
    if (!focusPostId || loading) return;
    const event = events.find(item => item.id === focusPostId);
    if (!event) return;
    const eventDate = new Date(event.scheduledAt || event.publishedAt || Date.now());
    if (!isSameMonth(eventDate, selectedDate)) {
      updateSchedulerParams({ date: eventDate });
      return;
    }
    setSelectedEvent(event);
    const params = new URLSearchParams(searchParams);
    params.delete('focus');
    setSearchParams(params, { replace: true });
  }, [focusPostId, events, loading]);

  function updateSchedulerParams(next) {
    const params = new URLSearchParams(searchParams);
    if (next.view) params.set('view', next.view);
    if (next.date) params.set('date', dateKey(next.date));
    if (next.status) params.set('status', next.status);
    setSearchParams(params, { replace: true });
  }

  function movePeriod(delta) {
    const next = view === 'month' || view === 'agenda' ? addMonths(selectedDate, delta) : view === 'week' ? addDays(selectedDate, delta * 7) : addDays(selectedDate, delta);
    updateSchedulerParams({ date: next });
  }

  function openCreateForDate(day) {
    sessionStorage.setItem('scheduler_create_prefill', JSON.stringify({ scheduledAt: defaultScheduledAt(day, timezone), timezone }));
    navigate('create');
  }

  function openEvent(event) {
    if (event.rawContent && openPost) openPost(event.rawContent);
    else setSelectedEvent(event);
  }

  async function refreshEvent(id, updater, successKey) {
    setWorkingId(id);
    setActionError(null);
    try {
      const result = await updater();
      if (result) {
        const mapped = mapSchedulerEvent(result, timezone);
        setEvents(previous => previous.map(item => item.id === id ? mapped : item).sort(compareEvents));
        setSelectedEvent(mapped);
      }
      if (successKey) notify(successKey);
    } catch (err) {
      setActionError(friendlyErrorMessage(err, 'Scheduler action failed. Try again.'));
    } finally {
      setWorkingId(null);
    }
  }

  async function rescheduleEvent(event, scheduledAt) {
    await refreshEvent(event.id, () => api.scheduler.reschedule(event.id, { scheduledAt, timezone }), 'scheduler.rescheduled');
  }

  async function unscheduleEvent(event) {
    setWorkingId(event.id);
    setActionError(null);
    try {
      await api.scheduler.unschedule(event.id);
      setEvents(previous => previous.filter(item => item.id !== event.id));
      setSelectedEvent(null);
      notify('scheduler.unscheduled');
    } catch (err) {
      setActionError(friendlyErrorMessage(err, 'Post was not unscheduled. Try again.'));
    } finally {
      setWorkingId(null);
    }
  }

  async function retryEvent(event, failedOnly = false) {
    await refreshEvent(event.id, () => api.scheduler.retry(event.id, { failedOnly }), 'scheduler.retryStarted');
  }

  async function deleteEvent(event) {
    setWorkingId(event.id);
    setActionError(null);
    try {
      await api.scheduler.delete(event.id);
      setEvents(previous => previous.filter(item => item.id !== event.id));
      setSelectedEvent(null);
      notify('scheduler.deleted');
    } catch (err) {
      setActionError(friendlyErrorMessage(err, 'Post was not deleted. Try again.'));
    } finally {
      setWorkingId(null);
    }
  }

  async function cloneEvent(event, payload) {
    setWorkingId(event.id);
    setActionError(null);
    try {
      const result = await api.scheduler.clone(event.id, payload);
      const mapped = mapSchedulerEvent(result, timezone);
      setEvents(previous => [...previous, mapped].sort(compareEvents));
      setSelectedEvent(null);
      notify('scheduler.cloned');
    } catch (err) {
      setActionError(friendlyErrorMessage(err, 'Clone was not created. Try again.'));
    } finally {
      setWorkingId(null);
    }
  }

  const rangeTitle = titleForView(view, selectedDate);
  const dayHeaders = weekDays(selectedDate);
  const monthDays = buildMonthDays(selectedDate);
  const agendaDays = buildAgendaDays(view, selectedDate, visibleRange);

  return (
    <div className={`page col scheduler-page-v2 scheduler-view-${view}`} style={{gap:16}}>
      <div className="scheduler-controls">
        <DeviceTimezoneLabel timezone={timezone} />
        <div className="dt-tabs scheduler-status-tabs">
          {STATUS_FILTERS.map(([key, labelKey]) => (
            <button key={key} className={'dt-tab ' + (filter === key ? 'is-active' : '')} onClick={() => updateSchedulerParams({ status: key })}>
              {t(`scheduler.${labelKey}`)} <span className="filter-count">{counts[key] || 0}</span>
            </button>
          ))}
        </div>
        <div className="scheduler-nav-row">
          <div className="seg scheduler-view-switcher">
            {VIEW_MODES.map(mode => <button key={mode} className={view === mode ? 'on' : ''} onClick={() => updateSchedulerParams({ view: mode })}>{t(`scheduler.view_${mode}`)}</button>)}
          </div>
          <div className="scheduler-period-nav">
            <button className="btn btn-icon" onClick={() => movePeriod(-1)} aria-label={t('scheduler.previousPeriod')}><Icon name="chev_l" size={14}/></button>
            <div className="scheduler-period-title"><strong>{rangeTitle}</strong><span>{t('scheduler.postsCount', { count: visibleEvents.length })}</span></div>
            <button className="btn btn-icon" onClick={() => movePeriod(1)} aria-label={t('scheduler.nextPeriod')}><Icon name="chev_r" size={14}/></button>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => updateSchedulerParams({ date: new Date() })}>{t('scheduler.today')}</button>
        </div>
      </div>

      {error && <div className="auth-error scheduler-state"><Icon name="alert" size={14} /> <span>{t('scheduler.loadError')}</span><button className="btn btn-secondary btn-sm" onClick={() => updateSchedulerParams({ date: selectedDate })}>{t('scheduler.retry')}</button></div>}
      {loading && <div className="card scheduler-state"><Icon name="refresh" size={12}/> {t('scheduler.loading')}</div>}

      {!loading && !error && view === 'month' && (
        <MonthView
          days={monthDays}
          eventsByDate={eventsByDate}
          selectedDate={selectedDate}
          onSelectDay={date => { updateSchedulerParams({ date }); setInspectorOpen(true); }}
          onOpen={openEvent}
          onActions={setSelectedEvent}
          onCreate={openCreateForDate}
          highlightedId={selectedEvent?.id || null}
          inspectorOpen={inspectorOpen}
          onInspectorClose={() => setInspectorOpen(false)}
        />
      )}
      {!loading && !error && view === 'week' && (
        <WeekView days={dayHeaders} eventsByDate={eventsByDate} selectedDate={selectedDate} onSelect={date => updateSchedulerParams({ date })} onOpen={openEvent} onActions={setSelectedEvent} onCreate={openCreateForDate} highlightedId={selectedEvent?.id || null} />
      )}
      {!loading && !error && view === 'day' && (
        <DayList day={selectedDate} events={eventsByDate[dateKey(selectedDate)] || []} onOpen={openEvent} onActions={setSelectedEvent} onCreate={openCreateForDate} highlightedId={selectedEvent?.id || null} />
      )}
      {!loading && !error && view === 'agenda' && (
        <AgendaView days={agendaDays} eventsByDate={eventsByDate} onOpen={openEvent} onActions={setSelectedEvent} onCreate={openCreateForDate} highlightedId={selectedEvent?.id || null} />
      )}
      {!loading && !error && visibleEvents.length === 0 && (
        <div className="card scheduler-empty-state">
          <Icon name="calendar" size={22}/>
          <strong>{filter === 'all' ? t('scheduler.emptyAllTitle', { month: formatMonthYear(selectedDate) }) : t('scheduler.emptyFilteredTitle', { status: STATUS_LABELS[filter] || filter, month: formatMonthYear(selectedDate) })}</strong>
          <span>{filter === 'all' ? t('scheduler.emptyAllHint') : t('scheduler.emptyFilteredHint')}</span>
          <div className="row" style={{gap:8}}>
            {filter !== 'all' && <button className="btn btn-secondary" onClick={() => updateSchedulerParams({ status: 'all' })}>{t('scheduler.clearFilter')}</button>}
            <button className="btn btn-primary" onClick={() => openCreateForDate(selectedDate)}><Icon name="plus" size={14}/> {t('scheduler.createPost')}</button>
          </div>
        </div>
      )}

      {selectedEvent && (
        <ScheduledPostModal
          event={selectedEvent}
          working={workingId === selectedEvent.id}
          error={actionError}
          timezone={timezone}
          onClose={() => { setSelectedEvent(null); setActionError(null); }}
          onPreview={() => openEvent(selectedEvent)}
          onReschedule={scheduledAt => rescheduleEvent(selectedEvent, scheduledAt)}
          onUnschedule={() => unscheduleEvent(selectedEvent)}
          onRetry={failedOnly => retryEvent(selectedEvent, failedOnly)}
          onDelete={() => deleteEvent(selectedEvent)}
          onClone={payload => cloneEvent(selectedEvent, payload)}
        />
      )}

    </div>
  );
};

function MonthView({ days, eventsByDate, selectedDate, onSelectDay, onOpen, onActions, onCreate, highlightedId, inspectorOpen, onInspectorClose }) {
  const selectedDay = days.find(day => isSameDay(day, selectedDate)) || selectedDate;
  const selectedEvents = eventsByDate[dateKey(selectedDay)] || [];
  return (
    <div className="scheduler-month-workspace">
      <div className="scheduler-month-layout">
        <div className="scheduler-month-frame">
          <div className="card scheduler-card scheduler-month-card" style={{ padding: 0 }}>
            <div className="scheduler-month-board">
              <CalendarHeader />
              <div className="cal-grid">
                {days.map(day => (
                  <CalendarCell
                    key={dateKey(day)}
                    day={day}
                    muted={!isSameMonth(day, selectedDate)}
                    selected={isSameDay(day, selectedDay)}
                    events={eventsByDate[dateKey(day)] || []}
                    onSelect={onSelectDay}
                    onCreate={onCreate}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <MonthDayInspector
          day={selectedDay}
          events={selectedEvents}
          onOpen={onOpen}
          onActions={onActions}
          onCreate={onCreate}
          isOpen={inspectorOpen}
          onClose={onInspectorClose}
        />
      </div>
    </div>
  );
}

function MonthDayInspector({ day, events, onOpen, onActions, onCreate, isOpen, onClose }) {
  const { t } = useTranslation();
  const statusCounts = dayStatusCounts(events);
  return (
    <>
      {isOpen && <div className="inspector-scrim" onClick={onClose} />}
      <aside className={'month-day-inspector' + (isOpen ? ' is-open' : '')} aria-label={t('scheduler.postsForDay', { date: formatLongDate(day) })}>
        <button className="inspector-close" onClick={onClose} aria-label={t('scheduler.closeInspector')}><Icon name="x" size={16} /></button>
        <div className="month-day-inspector-head">
          <div>
            <div className="eyebrow">{t('scheduler.selectedDay')}</div>
            <h3>{formatLongDate(day)}</h3>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => onCreate(day)}><Icon name="plus" size={13} /> {t('scheduler.new')}</button>
        </div>
        <div className="month-day-inspector-counts">
          <div className="month-day-count-card"><strong>{events.length}</strong><span>{t('scheduler.postWord', { count: events.length })}</span></div>
          <div className="month-day-status-summary">
            {statusCounts.length ? statusCounts.map(([status, count]) => (
              <span key={status}><StatusBadge status={status} compact /> <strong>{count}</strong></span>
            )) : <span className="caption">{t('scheduler.noScheduledPostsYet')}</span>}
          </div>
        </div>
        <div className="month-inspector-posts">
          {events.length ? events.map(event => (
            <MonthInspectorPost key={event.id} event={event} onOpen={onOpen} onActions={onActions} />
          )) : (
            <div className="month-day-empty-panel">
              <Icon name="calendar" size={18} />
              <span>{t('scheduler.dayOpenHint')}</span>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

function MonthInspectorPost({ event, onOpen, onActions }) {
  const { t } = useTranslation();
  function open() { onOpen(event); }
  return (
    <div
      className={'month-inspector-post ' + event.status}
      role="button"
      tabIndex={0}
      style={{ '--platform-accent': platformColor(event.platform) }}
      onClick={open}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
    >
      <div className="month-inspector-post-top">
        <span className="month-inspector-time">{event.time}</span>
        <span className="month-inspector-platforms">{event.platforms.map(platform => <Platform key={platform} name={platform} size={15} />)}</span>
        <StatusBadge status={event.status} missedWhileLapsed={event.missedWhileLapsed} />
        <button className="event-actions-btn" onClick={e => { e.stopPropagation(); onActions(event); }} aria-label={t('scheduler.postActions')}><Icon name="more" size={13} /></button>
      </div>
      <div className="month-inspector-title">{event.title}</div>
      <div className="month-inspector-meta">{event.targetSummary}</div>
    </div>
  );
}

function WeekView({ days, eventsByDate, selectedDate, onSelect, onOpen, onActions, onCreate, highlightedId }) {
  return <div className="card scheduler-card scheduler-week-card" style={{padding:0}}><div className="week-day-strip">{days.map(day => <button key={dateKey(day)} className={isSameDay(day, selectedDate) ? 'is-active' : ''} onClick={() => onSelect(day)}><span>{formatWeekday(day)}</span><strong>{day.getDate()}</strong></button>)}</div><div className="cal-grid cal-grid-week">{days.map(day => <CalendarCell key={dateKey(day)} day={day} selected={isSameDay(day, selectedDate)} events={eventsByDate[dateKey(day)] || []} onSelect={onSelect} onOpen={onOpen} onActions={onActions} onCreate={onCreate} highlightedId={highlightedId} />)}</div></div>;
}

function AgendaView({ days, eventsByDate, onOpen, onActions, onCreate, highlightedId }) {
  return <div className="scheduler-agenda">{days.map(day => <DayList key={dateKey(day)} day={day} events={eventsByDate[dateKey(day)] || []} onOpen={onOpen} onActions={onActions} onCreate={onCreate} highlightedId={highlightedId} compact />)}</div>;
}

function DayList({ day, events, onOpen, onActions, onCreate, compact, highlightedId }) {
  const { t } = useTranslation();
  return <div className="agenda-day"><div className="agenda-day-header">{formatLongDate(day)}</div>{events.length ? events.map(event => <EventRow key={event.id} event={event} onOpen={onOpen} onActions={onActions} isHighlighted={event.id === highlightedId} />) : <div className="scheduler-day-empty">{t('scheduler.noPostsScheduled')} <button className="btn btn-ghost btn-sm" onClick={() => onCreate(day)}><Icon name="plus" size={13}/> {t('scheduler.createPost')}</button></div>}{!compact && <button className="btn btn-secondary scheduler-create-day" onClick={() => onCreate(day)}><Icon name="plus" size={14}/> {t('scheduler.createPostForDay')}</button>}</div>;
}

function CalendarHeader() {
  const { t } = useTranslation();
  const labels = ['MON','TUE','WED','THU','FRI','SAT','SUN'];
  return <div className="cal-head">{labels.map((day, idx) => <div key={day} className={'cal-head-cell' + (idx >= 5 ? ' is-weekend' : '')}>{t(`scheduler.weekday_${day}`)}</div>)}</div>;
}

function CalendarCell({ day, muted, selected, events, onSelect, onCreate }) {
  const { t } = useTranslation();
  const dayOfWeek = day.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const statusBars = dayStatusBars(events);
  const statusSummary = dayStatusSummary(events, t);
  const next = events[0] || null;
  const nextStatusLabel = next ? schedulerStatusLabel(next.status, t, true) : '';

  function selectDay() { if (onSelect) onSelect(day); }

  const classes = [
    'cal-cell',
    muted && 'muted',
    selected && 'is-selected',
    isToday(day) && 'is-today',
    isWeekend && 'is-weekend',
    events.length > 0 && 'has-events',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      role="button"
      tabIndex={0}
      onClick={selectDay}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectDay(); } }}
      onDoubleClick={() => onCreate(day)}
      aria-label={`${formatLongDate(day)}${events.length ? t('scheduler.cellPostsCount', { count: events.length }) : t('scheduler.cellNoPosts')}`}
    >
      <div className="cal-cell-head">
        <span className="cal-day-num">{day.getDate()}</span>
        <button className="cal-create-btn" onClick={e => { e.stopPropagation(); onCreate(day); }} aria-label={t('scheduler.createPostForDate', { date: formatLongDate(day) })}>
          <Icon name="plus" size={12} />
        </button>
      </div>
      {events.length > 0 && (
        <div className="cal-day-overview">
          <div className="cal-day-status-stack" aria-hidden="true">
            {statusBars.map(bar => (
              <span key={bar.status} className={bar.status} style={{ '--w': bar.weight }} />
            ))}
          </div>
          <div className="cal-day-brief">
            <span className="cal-day-total">{events.length} {t('scheduler.postWord', { count: events.length })}</span>
            <span className="cal-day-mix">{statusSummary}</span>
          </div>
          {next && (
            <div className="cal-day-next" data-status={next.status}>
              <div className="cal-day-next-meta">
                <span className="cal-day-next-time">{next.time}</span>
                <span className="cal-day-next-platforms">
                  {next.platforms.slice(0, 3).map(platform => <Platform key={platform} name={platform} size={12} />)}
                </span>
              </div>
              <div className="cal-day-next-status-row"><span className="cal-day-next-status">{nextStatusLabel}</span></div>
              <div className="cal-day-next-title">{next.title}</div>
              {events.length > 1 && <div className="cal-day-more">{t('scheduler.moreCount', { count: events.length - 1 })}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EventRow({ event, onOpen, onActions, isHighlighted }) {
  const { t } = useTranslation();
  return <div className={'agenda-item ' + event.accent + (isHighlighted ? ' is-highlighted' : '')} role="button" tabIndex={0} onClick={() => onOpen(event)} onKeyDown={e => e.key === 'Enter' && onOpen(event)}><div className="agenda-item-time">{event.time}</div><div className="agenda-item-platforms">{event.platforms.map(platform => <Platform key={platform} name={platform} size={14}/>)}</div><div className="agenda-item-title">{event.title}</div><StatusBadge status={event.status} missedWhileLapsed={event.missedWhileLapsed} className="agenda-item-status"/><button className="event-actions-btn" onClick={e => { e.stopPropagation(); onActions(event); }} aria-label={t('scheduler.postActions')}><Icon name="more" size={13}/></button></div>;
}

function StatusBadge({ status, className = '', compact = false, missedWhileLapsed = false }) {
  const { t } = useTranslation();
  // TASK-331 phase-1-fixes round 4 (fix 1): the only remaining trace of the
  // retired 'missed' status — a plain 'failed' post whose target(s) carry
  // last_error_code='missed_while_lapsed' gets this distinct, localized label
  // instead of the generic plain-English "Failed"/"Fail"/"FAILED".
  const label = status === 'inbox'
    ? t('postPreview.inboxDraftTitle')
    : status === 'failed' && missedWhileLapsed
      ? t('scheduler.statusMissedWhileLapsed')
      : compact ? STATUS_SHORT_LABELS[status] || STATUS_LABELS[status] || status : STATUS_LABELS[status] || status;
  return <span className={'cal-status ' + status + ' ' + (compact ? 'is-compact ' : '') + className}>{label}</span>;
}

function ScheduledPostModal({ event, working, error, timezone, onClose, onPreview, onReschedule, onUnschedule, onRetry, onDelete, onClone }) {
  const { t } = useTranslation();
  const [scheduledAt, setScheduledAt] = useState(toDatetimeLocalValue(event.scheduledAt || new Date().toISOString()));
  const [cloneOpen, setCloneOpen] = useState(false);
  // TASK-331 phase-1-fixes round 4 (fix 2): 'blocked_billing' included so a
  // parked post (a lapsed org's post the publish-dispatcher parked, reversibly)
  // can be rescheduled even before the renewal sweep un-parks it —
  // scheduler-worker's handleReschedulePost already accepts 'blocked_billing'
  // in its status IN(...) guard (src/index.ts ~1157/1189), and the backend
  // un-parks the parent target on reschedule; cancel was already allowed
  // (destructiveDisabled below never gates 'blocked_billing').
  const canMove = ['approved', 'scheduled', 'failed', 'partial_failure', 'blocked_billing'].includes(event.status);
  const canRetry = ['failed', 'partial_failure'].includes(event.status) && event.canRetry;
  const destructiveDisabled = ['publishing', 'queued'].includes(event.status);

  useEffect(() => { setScheduledAt(toDatetimeLocalValue(event.scheduledAt || new Date().toISOString())); setCloneOpen(false); }, [event.id, event.scheduledAt]);

  return <div className="modal-scrim" onClick={onClose}><div className="modal scheduled-post-modal" onClick={e => e.stopPropagation()}><button className="modal-x" onClick={onClose} aria-label={t('scheduler.closeScheduledPost')}><Icon name="x" size={16}/></button><div className="eyebrow">{t('scheduler.scheduledPost')}</div><h2 className="h2" style={{marginTop:6}}>{event.title}</h2><div className="row" style={{gap:6, flexWrap:'wrap', marginTop:12}}><StatusBadge status={event.status} missedWhileLapsed={event.missedWhileLapsed}/>{event.platforms.map(platform => <Platform key={platform} name={platform} size={18}/>)}<span className="chip">{event.time} · {timezone}</span></div><p className="caption" style={{marginTop:12}}>{event.lifecycleSummary}</p>{error && <div className="auth-error" style={{marginTop:12}}><Icon name="alert" size={14}/> {error}</div>}<div className="scheduler-action-grid"><button className="btn btn-primary" onClick={onPreview}>{t('scheduler.openPreview')}</button><button className="btn btn-secondary" onClick={() => navigator.clipboard?.writeText(event.contentPreview || event.title)}>{t('scheduler.copyText')}</button><button className="btn btn-secondary" onClick={() => setCloneOpen(value => !value)}>{t('scheduler.cloneToPlatforms')}</button>{canRetry && <button className="btn btn-secondary" disabled={working} onClick={() => onRetry(event.status === 'partial_failure')}>{working ? t('scheduler.retrying') : event.status === 'partial_failure' ? t('scheduler.retryFailedPlatforms') : t('scheduler.retryPublishing')}</button>}</div>{event.errorDetails.length > 0 && <details className="scheduler-secondary-actions"><summary>{t('scheduler.viewErrors')}</summary><div className="scheduler-error-list">{event.errorDetails.map((item, index) => <div key={`${item.platform}-${index}`}><strong>{platformLabel(item.platform)}</strong><span>{item.message || t('scheduler.publishingFailed')}</span></div>)}</div></details>}{cloneOpen && <ClonePanel event={event} timezone={timezone} working={working} onClone={onClone}/>}<details className="scheduler-secondary-actions" open><summary>{t('scheduler.scheduleActions')}</summary><DeviceTimezoneLabel timezone={timezone} /><div className="grid-2-eq" style={{marginTop:12}}><input className="input" type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} disabled={!canMove}/><button className="btn btn-secondary" disabled={!canMove || working || !datetimeLocalToIso(scheduledAt)} onClick={() => onReschedule(datetimeLocalToIso(scheduledAt))}>{t('scheduler.reschedule')}</button></div><div className="row" style={{gap:8, marginTop:10, flexWrap:'wrap'}}><button className="btn btn-ghost" disabled={!canMove || working || destructiveDisabled} onClick={onUnschedule}>{t('scheduler.unschedule')}</button><button className="btn btn-ghost" disabled={working || destructiveDisabled} onClick={onDelete}>{t('scheduler.delete')}</button><button className="btn btn-ghost" disabled>{t('scheduler.edit')}</button></div></details></div></div>;
}

function ClonePanel({ event, timezone, working, onClone }) {
  const { t } = useTranslation();
  const [platforms, setPlatforms] = useState(event.platforms);
  const [mode, setMode] = useState('same_content');
  const [scheduleMode, setScheduleMode] = useState('schedule');
  const [scheduledAt, setScheduledAt] = useState(toDatetimeLocalValue(event.scheduledAt || new Date().toISOString()));
  const platformOptions = ['instagram', 'facebook', 'linkedin', 'telegram', 'x'];
  return <div className="scheduler-clone-panel"><h3>{t('scheduler.clonePostToPlatforms')}</h3><p className="caption">{t('scheduler.cloneOriginal', { platforms: event.platforms.map(platformLabel).join(', '), time: event.time })}</p><div className="scheduler-platform-options">{platformOptions.map(platform => <label key={platform}><input type="checkbox" checked={platforms.includes(platform)} onChange={() => setPlatforms(value => value.includes(platform) ? value.filter(item => item !== platform) : [...value, platform])}/>{platformLabel(platform)}</label>)}</div><div className="scheduler-radio-row"><label><input type="radio" checked={mode === 'same_content'} onChange={() => setMode('same_content')}/> {t('scheduler.useSameContent')}</label><label><input type="radio" checked={mode === 'adapt_per_platform'} onChange={() => setMode('adapt_per_platform')} disabled/> {t('scheduler.adaptWithAiSoon')}</label></div><div className="scheduler-radio-row"><label><input type="radio" checked={scheduleMode === 'schedule'} onChange={() => setScheduleMode('schedule')}/> {t('scheduler.scheduleLater')}</label><label><input type="radio" checked={scheduleMode === 'publish_now'} onChange={() => setScheduleMode('publish_now')}/> {t('scheduler.publishNow')}</label></div>{scheduleMode === 'schedule' && <label><DeviceTimezoneLabel timezone={timezone} /><input className="input" type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}/></label>}<button className="btn btn-primary" disabled={!platforms.length || working} onClick={() => onClone({ targetPlatforms: platforms, mode, scheduleMode, scheduledAt: scheduleMode === 'schedule' ? datetimeLocalToIso(scheduledAt) : null, timezone })}>{t('scheduler.createClone')}</button></div>;
}

function mapSchedulerEvent(row, timezone) {
  const post = row.post || row;
  const targets = row.targets || [];
  const content = row.content || null;
  const scheduledAt = post.scheduledAt || post.scheduled_at || targets.find(target => target.scheduledAt)?.scheduledAt;
  const publishedAt = post.publishedAt || post.published_at || targets.find(target => target.publishedAt)?.publishedAt;
  const displayDate = localDateParts(scheduledAt || publishedAt || new Date().toISOString(), timezone);
  const status = normalizeEventStatus(post.status || 'approved', targets);
  const platforms = uniquePlatforms(targets);
  const errorDetails = targets.filter(isFailedTarget).map(target => ({ platform: target.platform, message: target.lastErrorMessage || target.errorMessage || target.last_error_message, code: target.lastErrorCode, retryable: target.status !== 'needs_reconnect' }));
  const finalPlatforms = platforms.length ? platforms : ['linkedin'];
  // TASK-331 phase-1-fixes round 4 (fix 1): the retired 'missed' status is now
  // a plain 'failed' target stamped with last_error_code='missed_while_lapsed'
  // — flagged here (not folded into `status`) so StatusBadge can swap in a
  // distinct label while every filter/count/legend still buckets it under the
  // ordinary 'failed' status. `every`, not `some`: a post mixing a genuine
  // failure with a missed-while-lapsed target still needs the generic
  // "Failed" treatment (it has a real problem beyond the lapsed skip).
  const missedWhileLapsed = status === 'failed' && errorDetails.length > 0 && errorDetails.every(item => item.code === MISSED_WHILE_LAPSED_ERROR_CODE);
  return { id: post.id, raw: row, rawContent: content, contentPreview: content?.activeVersion?.body || content?.activeVersion?.caption || content?.contentItem?.title || '', scheduledAt, publishedAt, timezone: post.timezone || timezone, localDateKey: displayDate.key, time: displayDate.time, title: scheduledTitle(post, targets, content), targetSummary: scheduledTargetSummary(targets), lifecycleSummary: lifecycleSummary(post, targets), platforms: finalPlatforms, platform: finalPlatforms[0], status, missedWhileLapsed, accent: status === 'failed' || status === 'partial_failure' ? 'red' : status === 'published' ? 'published' : platforms.includes('instagram') ? 'violet' : 'gold', canRetry: errorDetails.some(item => item.retryable), failedPlatforms: errorDetails.map(item => item.platform), publishedPlatforms: targets.filter(target => target.status === 'published').map(target => target.platform), errorDetails };
}

function platformColor(platform) {
  switch (platform) {
    case 'instagram': return '#DD2A7B';
    case 'tiktok': return '#26C9D5';
    case 'linkedin': return '#0A66C2';
    case 'facebook': return '#1877F2';
    case 'telegram': return '#229ED9';
    case 'x':
    case 'twitter': return '#9CA3AF';
    case 'threads': return '#A78BFA';
    default: return '#8B7AFF';
  }
}

function normalizeEventStatus(status, targets) {
  const hasPublished = targets.some(target => target.status === 'published');
  const failed = targets.filter(isFailedTarget);
  if (hasPublished && failed.length) return 'partial_failure';
  // TASK-331 phase-1-fixes round 4: the round-3 'missed' status is retired —
  // a past-dated target the renewal sweep can no longer publish now settles
  // as a plain 'failed' target (last_error_code='missed_while_lapsed'), so it
  // is caught by isFailedTarget above with no separate branch needed here.
  // See mapSchedulerEvent's `missedWhileLapsed` flag for the label override.
  if (failed.length) return 'failed';
  if (targets.some(target => ['queued', 'publishing'].includes(target.status))) return 'publishing';
  if (targets.some(target => target.status === 'needs_manual_action') && targets.every(target => ['published', 'needs_manual_action'].includes(target.status))) return 'inbox';
  if (targets.length && targets.every(target => target.status === 'published')) return 'published';
  if (status === 'queued') return 'publishing';
  return status || 'approved';
}

function isFailedTarget(target) { return ['failed', 'needs_reconnect'].includes(target.status); }
function scheduledTitle(post, targets, content) { return content?.contentItem?.title || content?.activeVersion?.hook || content?.contentItem?.topic || `${targets[0] ? platformLabel(targets[0].platform) : 'Scheduled'} post`; }
function scheduledTargetSummary(targets) { const labels = targets.map(target => target.socialAccount?.displayName || platformLabel(target.platform)); return labels.length <= 3 ? labels.join(', ') || 'No targets' : `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`; }
function lifecycleSummary(post, targets) { if (!targets.length) return STATUS_LABELS[post.status] || post.status || 'No targets'; const failed = targets.filter(isFailedTarget); const inbox = targets.find(target => target.status === 'needs_manual_action'); const published = targets.filter(target => target.status === 'published'); if (published.length && failed.length) return `${published.length} published, ${failed.length} failed`; if (failed.length) return failed[0].lastErrorMessage || `${failed.length} failed`; if (inbox) return inbox.lastErrorMessage || inbox.errorMessage || inbox.last_error_message || STATUS_LABELS.inbox; if (published.length) return `${published.length} published`; return targets.map(target => `${platformLabel(target.platform)} ${STATUS_LABELS[target.status] || target.status}`).join(', '); }
function uniquePlatforms(targets) { return Array.from(new Set(targets.map(target => target.platform).filter(Boolean))); }
function compareEvents(a, b) { return String(a.scheduledAt || '').localeCompare(String(b.scheduledAt || '')); }
function groupEventsByDate(items) { return items.reduce((acc, item) => { (acc[item.localDateKey] = acc[item.localDateKey] || []).push(item); return acc; }, {}); }
function countStatuses(items) { const counts = { all: items.length, approved: 0, publishing: 0, published: 0, failed: 0, partial_failure: 0, blocked_billing: 0 }; for (const item of items) counts[item.status] = (counts[item.status] || 0) + 1; return counts; }
// NOTE: dayStatusTone/dayStatusDots are DEAD — nothing in the SPA calls either
// (verified 2026-08-30). They also do not know about 'inbox'. Left untouched
// on purpose: "fixing" unreachable code would look like a fix and change
// nothing. The live equivalent is `accent` in the event mapper above, which
// gives an inbox event the same gold as a routine scheduled one — that one
// needs a colour decision, so it is filed rather than guessed at.
function dayStatusTone(events) { if (!events.length) return 'empty'; if (events.some(event => ['failed', 'partial_failure'].includes(event.status))) return 'danger'; if (events.some(event => ['publishing', 'queued'].includes(event.status))) return 'live'; if (events.every(event => event.status === 'published')) return 'published'; return 'scheduled'; }
function dayStatusDots(events) { const preferred = ['failed', 'partial_failure', 'publishing', 'published', 'approved', 'scheduled']; const statuses = new Set(events.map(event => event.status)); return preferred.filter(status => statuses.has(status)).slice(0, 3); }
function dayStatusBars(events) {
  // 'inbox' (needs_manual_action) sits right after the hard failures: it needs
  // attention sooner than routine publishing/published progress, but a true
  // failure still outranks it. 'blocked_billing' (TASK-331) sits right after
  // it — informational (renewing fixes it), ranked above routine progress.
  // (round 4: 'missed' retired — a missed-while-lapsed post is now a plain
  // 'failed' event and already ranks via the 'failed' entry above.)
  const order = ['failed', 'partial_failure', 'inbox', 'blocked_billing', 'publishing', 'queued', 'published', 'approved', 'scheduled', 'draft'];
  const counts = {};
  for (const event of events) counts[event.status] = (counts[event.status] || 0) + 1;
  return order.filter(status => counts[status]).map(status => ({ status, weight: counts[status] }));
}
function schedulerStatusLabel(status, t, short = false) { return status === 'inbox' ? t('postPreview.inboxDraftTitle') : (short ? STATUS_PILL_LABELS[status] : STATUS_LABELS[status]) || status; }
function dayStatusSummary(events, t) { return dayStatusCounts(events).slice(0, 2).map(([status, count]) => `${count} ${String(schedulerStatusLabel(status, t)).toLowerCase()}`).join(' · '); }
function dayStatusCounts(events) { const order = ['failed', 'partial_failure', 'publishing', 'published', 'approved', 'scheduled', 'draft']; const counts = {}; for (const event of events) counts[event.status] = (counts[event.status] || 0) + 1; const statuses = [...order.filter(status => counts[status]), ...Object.keys(counts).filter(status => !order.includes(status))]; return statuses.map(status => [status, counts[status]]); }
function statusFilterMatch(status, filter) { return filter === 'publishing' ? ['publishing', 'queued'].includes(status) : status === filter; }
function normalizeFilter(value) { return STATUS_FILTERS.some(([key]) => key === value) ? value : 'all'; }
function normalizeView(value, fallback) { return VIEW_MODES.includes(value) ? value : fallback; }
function parseDateParam(value) { if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null; const [year, month, day] = value.split('-').map(Number); return new Date(year, month - 1, day); }
function startOfDay(date) { return new Date(date.getFullYear(), date.getMonth(), date.getDate()); }
function startOfMonth(date) { return new Date(date.getFullYear(), date.getMonth(), 1); }
function endOfMonth(date) { return new Date(date.getFullYear(), date.getMonth() + 1, 0); }
function startOfWeek(date) { const day = date.getDay(); const diff = (day - WEEK_START + 7) % 7; return addDays(startOfDay(date), -diff); }
function endOfWeek(date) { return addDays(startOfWeek(date), 6); }
function addDays(date, days) { const next = new Date(date); next.setDate(next.getDate() + days); return next; }
function addMonths(date, months) { return new Date(date.getFullYear(), date.getMonth() + months, Math.min(date.getDate(), 28)); }
function eachDay(start, end) { const days = []; for (let day = startOfDay(start); day <= end; day = addDays(day, 1)) days.push(day); return days; }
function buildMonthDays(date) { return eachDay(startOfWeek(startOfMonth(date)), endOfWeek(endOfMonth(date))); }
function weekDays(date) { return eachDay(startOfWeek(date), endOfWeek(date)); }
function rangeForView(view, date) { if (view === 'week') return { start: startOfWeek(date), end: endOfWeek(date) }; if (view === 'day') return { start: startOfDay(date), end: startOfDay(date) }; return { start: startOfWeek(startOfMonth(date)), end: endOfWeek(endOfMonth(date)) }; }
function calendarFetchRange(date) { return { from: startOfWeek(startOfMonth(date)), to: addDays(endOfWeek(endOfMonth(date)), 1) }; }
function buildAgendaDays(view, date, range) { return view === 'agenda' ? eachDay(startOfMonth(date), endOfMonth(date)) : eachDay(range.start, range.end); }
function dateKey(date) { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`; }
function isSameDay(a, b) { return dateKey(a) === dateKey(b); }
function isSameMonth(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth(); }
function isToday(date) { return isSameDay(date, new Date()); }
function formatMonthYear(date) { return formatAppDate(date, { month: 'long', year: 'numeric' }); }
function formatLongDate(date) { return formatAppDate(date, { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' }); }
function formatWeekday(date) { return formatAppDate(date, { weekday: 'short' }); }
function titleForView(view, date) { if (view === 'week') return `${formatLongDate(startOfWeek(date))} - ${formatLongDate(endOfWeek(date))}`; if (view === 'day') return formatLongDate(date); return formatMonthYear(date); }
function titleCase(value) { return value.slice(0, 1).toUpperCase() + value.slice(1); }
function localDateParts(value, timezone) { const date = new Date(value); const safe = Number.isNaN(date.getTime()) ? new Date() : date; const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(safe).filter(part => part.type !== 'literal').map(part => [part.type, part.value])); return { key: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}` }; }
function defaultScheduledAt(day) { const date = new Date(day); date.setHours(9, 0, 0, 0); return date.toISOString(); }
function toDatetimeLocalValue(value) { const date = new Date(value); const safeDate = Number.isNaN(date.getTime()) ? new Date(Date.now() + 15 * 60 * 1000) : date; const local = new Date(safeDate.getTime() - safeDate.getTimezoneOffset() * 60000); return local.toISOString().slice(0, 16); }
function datetimeLocalToIso(value) { const date = new Date(value); return Number.isNaN(date.getTime()) ? null : date.toISOString(); }
function platformLabel(platform) { const labels = { instagram: 'Instagram', threads: 'Threads', telegram: 'Telegram', x: 'X', twitter: 'X', tiktok: 'TikTok', linkedin: 'LinkedIn', facebook: 'Facebook Page' }; return labels[platform] || platform; }
